/**
 * Analytics Configuration
 * 
 * Централизованная конфигурация модуля аналитики
 * Значения можно переопределить через переменные окружения
 */

import { BRAND_CONFIG } from '@@brand'

/**
 * Конфигурация аналитики
 */
export interface AnalyticsConfig {
  /** Валюта по умолчанию (ISO 4217) */
  currency: string
  /** ID Google Analytics 4 */
  gaId: string | undefined
  /** ID Яндекс.Метрики */
  ymId: number | null
  /** Включить логирование в dev режиме */
  debugMode: boolean
  /** Таймаут для дебаунсинга событий (ms) */
  debounceTimeout: number
  /** Максимальное количество событий в очереди */
  maxQueueSize: number
  /** Включить санитизацию PII */
  enablePIISanitization: boolean
}

type AnalyticsEnv = {
  DEV?: boolean
  VITE_ANALYTICS_CURRENCY?: string
  VITE_GA_ID?: string
  VITE_YM_ID?: string
  VITE_ANALYTICS_DEBUG?: string
  VITE_ANALYTICS_DEBOUNCE?: string
  VITE_ANALYTICS_QUEUE_SIZE?: string
  VITE_ANALYTICS_PII_SANITIZATION?: string
}

function warnInvalidEnv (message: string, isDev: boolean): void {
  if (isDev) {
    console.warn(`[Analytics config] ${message}`)
  }
}

function normalizeCurrency (value: string | undefined, fallback: string, isDev: boolean): string {
  if (!value) return fallback
  const normalized = value.trim().toUpperCase()
  if (/^[A-Z]{3}$/.test(normalized)) {
    return normalized
  }
  warnInvalidEnv(`Invalid VITE_ANALYTICS_CURRENCY: "${value}", using "${fallback}"`, isDev)
  return fallback
}

function normalizeGAId (value: string | undefined, isDev: boolean): string | undefined {
  if (!value) return undefined
  const normalized = value.trim()
  if (/^G-[A-Z0-9]+$/i.test(normalized)) {
    return normalized
  }
  warnInvalidEnv(`Invalid VITE_GA_ID: "${value}", analytics for GA4 disabled`, isDev)
  return undefined
}

function normalizeYMId (value: string | undefined, isDev: boolean): number | null {
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed
  }
  warnInvalidEnv(`Invalid VITE_YM_ID: "${value}", analytics for YM disabled`, isDev)
  return null
}

/**
 * Безопасное получение числового значения из env
 */
function getEnvNumber (value: string | undefined, defaultValue: number): number {
  if (!value) return defaultValue
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? defaultValue : parsed
}

function getEnvNumberInRange (
  value: string | undefined,
  defaultValue: number,
  min: number,
  max: number,
  envName: string,
  isDev: boolean,
): number {
  const parsed = getEnvNumber(value, defaultValue)
  if (parsed < min || parsed > max) {
    warnInvalidEnv(
      `Invalid ${envName}: "${value}". Expected ${min}-${max}, using ${defaultValue}`,
      isDev,
    )
    return defaultValue
  }
  return parsed
}

function getEnvBooleanWithValidation (
  value: string | undefined,
  defaultValue: boolean,
  envName: string,
  isDev: boolean,
): boolean {
  if (value === undefined) return defaultValue
  const normalized = value.trim().toLowerCase()
  if (normalized === 'true' || normalized === '1') return true
  if (normalized === 'false' || normalized === '0') return false
  warnInvalidEnv(`Invalid ${envName}: "${value}", using ${defaultValue}`, isDev)
  return defaultValue
}

/**
 * Конфигурация по умолчанию
 */
const defaultConfig: AnalyticsConfig = {
  currency: BRAND_CONFIG.currency.code,
  gaId: undefined,
  ymId: null,
  debugMode: false,
  debounceTimeout: 300,
  maxQueueSize: 100,
  enablePIISanitization: true,
}

/**
 * Загрузить конфигурацию из переменных окружения
 */
function loadConfig (): AnalyticsConfig {
  // В SSR окружении возвращаем дефолтные значения
  if (typeof import.meta === 'undefined') {
    return defaultConfig
  }
  
  const env = import.meta.env as AnalyticsEnv
  const isDev = Boolean(env.DEV)
  
  return {
    currency: normalizeCurrency(env.VITE_ANALYTICS_CURRENCY, defaultConfig.currency, isDev),
    gaId: normalizeGAId(env.VITE_GA_ID, isDev),
    ymId: normalizeYMId(env.VITE_YM_ID, isDev),
    debugMode: isDev || getEnvBooleanWithValidation(
      env.VITE_ANALYTICS_DEBUG,
      defaultConfig.debugMode,
      'VITE_ANALYTICS_DEBUG',
      isDev,
    ),
    debounceTimeout: getEnvNumberInRange(
      env.VITE_ANALYTICS_DEBOUNCE,
      defaultConfig.debounceTimeout,
      50,
      10000,
      'VITE_ANALYTICS_DEBOUNCE',
      isDev,
    ),
    maxQueueSize: getEnvNumberInRange(
      env.VITE_ANALYTICS_QUEUE_SIZE,
      defaultConfig.maxQueueSize,
      1,
      10000,
      'VITE_ANALYTICS_QUEUE_SIZE',
      isDev,
    ),
    enablePIISanitization: getEnvBooleanWithValidation(
      env.VITE_ANALYTICS_PII_SANITIZATION,
      defaultConfig.enablePIISanitization,
      'VITE_ANALYTICS_PII_SANITIZATION',
      isDev,
    ),
  }
}

/**
 * Текущая конфигурация аналитики
 */
export const analyticsConfig: AnalyticsConfig = loadConfig()

/**
 * Проверить, включен ли debug режим
 */
export function isDebugMode (): boolean {
  return analyticsConfig.debugMode
}

/**
 * Получить валюту по умолчанию
 */
export function getDefaultCurrency (): string {
  return analyticsConfig.currency
}

/**
 * Получить ID Google Analytics
 */
export function getGAId (): string | undefined {
  return analyticsConfig.gaId
}

/**
 * Получить ID Яндекс.Метрики
 */
export function getYMId (): number | null {
  return analyticsConfig.ymId
}

export default analyticsConfig
